import {
  faAt,
  faBriefcase,
  faChevronDown,
  faChevronUp,
  faFaceFrown,
  faGlobe,
  faMagicWandSparkles,
  faPerson,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "sonner";
import { useUploadLeadMatrix, useGetLeadMatrix, useDeleteLeadMatrix } from "../../../../../services/organtization/lead/matrix";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import {
  type PropsWithChildren,
  forwardRef,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import { trim } from "validator";
import type { Lead } from "../../../../../client";
import {
  Button,
  Image,
  ImageButton,
  Input,
  SecondarySectionTitle,
  TextButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  isEmailOrEmpty,
  isPhoneNumberOrEmpty,
  isString,
  isUrlOrEmpty,
  isUrlWithMaxLengthOrEmpty,
  type InputRef,
} from "../../../../../components";
import { BulletSectionTitle } from "../../../../../components/section/BulletSectionTitle";
import { getImageUrl } from "../../../../../utils/image";
import { getLeadSkillNames } from "../../../../../utils/get-lead-skills";
import { useAnimate, useStatsigClient } from "../../../../../hooks";
import { useGetOrganization } from "../../../../../services";
import { homeState } from "../../../../../state/home";
import { useGetLeadMatch } from "../../../../../services/organtization/get-lead-match";
import EditorSkillTagButton from "../../../../../components/atoms/EditorSkillTagButton";
import SkillsIcon from "../../../../../components/atoms/SkillsIcon";
import { LeadRowCard } from "../card/LeadRowCard";
import { LeadRowCardSection } from "../card/LeadRowCardSection";
import { LeadRowCardSectionWithLink } from "../card/LeadRowCardSectionWithLink";
import { cn } from "../../../../../components/utils/cn";
import MagicIcon from "../../../../../assets/magic_icon.png";

interface Props {
  lead?: Lead;
  disabled: boolean;
  onMutateLead: (lead: Partial<Lead>) => void;
  onRequestAssignmentInfo: (url: string) => void;
  onRequestAssignmentInfoWithInfo: (info: string) => void;
  onRequestWebsiteInfo: (url: string) => void;
  onSelectLogo: (logoUrl: string) => void;
  isEditingAssignmentLink: boolean;
  isEditingWebsiteLink: boolean;
  onEditAssigmentLinkClick: () => void;
  onEditWebsiteLinkClick: () => void;
  couldNotLoadFromUrl: boolean;
  couldNotLoadClientFromUrl: boolean;
  organizationId: string;
  onMatchingMembers: (numberOfMatchingMembers: number) => void;
  onArchive?: () => void;
}

const MAX_VISIBLE_SKILLS = 5;

const formatRemote = (value: string) => {
  switch (value) {
    case "remote":
      return "Remote";
    case "remote_possible":
      return "Remote possible";
    case "no_remote":
      return "No remote";
    default:
      return value;
  }
};

// ---------------------------------------------------------------------------
// Accordion section
// ---------------------------------------------------------------------------

interface AccordionSectionProps extends PropsWithChildren {
  title: string;
  defaultOpen?: boolean;
}

const AccordionSection = ({
  title,
  defaultOpen = false,
  children,
}: AccordionSectionProps) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-dashed border-gray-3 rounded-2xl">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-2"
        onClick={() => setOpen((v) => !v)}
      >
        <BulletSectionTitle className="py-0">{title}</BulletSectionTitle>
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className="text-gray-500 w-3 h-3"
        />
      </button>
      {open && <div className="px-4 pb-4 flex flex-col gap-4">{children}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const AssignmentAccordionPanel = ({
  lead,
  disabled,
  onMutateLead,
  onRequestAssignmentInfo,
  onRequestAssignmentInfoWithInfo,
  onRequestWebsiteInfo,
  onSelectLogo,
  isEditingAssignmentLink,
  isEditingWebsiteLink,
  onEditAssigmentLinkClick,
  onEditWebsiteLinkClick,
  couldNotLoadFromUrl,
  couldNotLoadClientFromUrl,
  organizationId,
  onMatchingMembers,
  onArchive,
}: Props) => {
  const [skillsParent] = useAnimate();
  const [expanded, toggleExpanded] = useReducer((v) => !v, false);

  const statsigClient = useStatsigClient();
  const showMatrix = statsigClient?.checkGate("competence_matrix") ?? false;

  const { data: orgData } = useGetOrganization(organizationId);
  const { data: skillMatchData } = useGetLeadMatch(
    {
      organizationId: lead?.orgId!,
      leadId: lead?.id ?? "",
      skills: getLeadSkillNames(lead),
    },
    { enabled: getLeadSkillNames(lead).length > 0 },
  );

  useEffect(() => {
    if (skillMatchData) onMatchingMembers(skillMatchData.length);
  }, [skillMatchData, onMatchingMembers]);

  const companyUrlInputRef = useRef<InputRef>(null);
  const matrixFileInputRef = useRef<HTMLInputElement>(null);

  const uploadMatrix = useUploadLeadMatrix(organizationId, lead?.id ?? "");
  const deleteMatrix = useDeleteLeadMatrix(organizationId, lead?.id ?? "");
  const { data: matrixData } = useGetLeadMatrix(organizationId, lead?.id ?? "");
  const hasMatrixTemplate = !!matrixData;

  const handleMatrixUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMatrix.mutate(file, {
      onSuccess: () => toast.success("Competence matrix uploaded"),
      onError: () => toast.error("Failed to upload competence matrix"),
    });
    e.target.value = "";
  };

  const handleCompanyUrlSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (companyUrlInputRef.current?.value) {
        onRequestWebsiteInfo(companyUrlInputRef.current.value);
      }
    },
    [onRequestWebsiteInfo],
  );

  const [localContactName, setLocalContactName] = useState(
    lead?.contactPersonName ?? "",
  );
  const [localContactEmail, setLocalContactEmail] = useState(
    lead?.contactPersonEmail ?? "",
  );
  const [localContactPhone, setLocalContactPhone] = useState(
    lead?.contactPersonPhone ?? "",
  );
  const [localCompanyName, setLocalCompanyName] = useState(
    lead?.companyName ?? "",
  );
  const [localNotes, setLocalNotes] = useState(lead?.notes ?? "");
  const [localAssignmentTitle, setLocalAssignmentTitle] = useState(
    lead?.assignmentInfo?.title ?? "",
  );

  useEffect(() => {
    setLocalContactName(lead?.contactPersonName ?? "");
    setLocalContactEmail(lead?.contactPersonEmail ?? "");
    setLocalContactPhone(lead?.contactPersonPhone ?? "");
    setLocalCompanyName(lead?.companyName ?? "");
    setLocalNotes(lead?.notes ?? "");
    setLocalAssignmentTitle(
      lead?.assignmentInfo?.title ?? lead?.parsedAssignmentData?.title ?? "",
    );
  }, [
    lead?.contactPersonName,
    lead?.contactPersonEmail,
    lead?.contactPersonPhone,
    lead?.companyName,
    lead?.notes,
    lead?.assignmentInfo,
    lead?.parsedAssignmentData,
  ]);

  const getProfilePhotoIds = (profileId: string) =>
    orgData?.profiles.filter((p) => p.profileId === profileId)[0]
      ?.profilePhotoId;

  const requiredSkills = () =>
    lead?.parsedAssignmentData?.skills?.technical_required ?? [];

  // -------------------------------------------------------------------------
  // Assignment section content
  // -------------------------------------------------------------------------

  const assignmentContent = (() => {
    if (disabled) {
      return <AssignmentLoadingState />;
    }

    if (lead?.parsedAssignmentData) {
      if (
        lead.parsedAssignmentData.skills &&
        !couldNotLoadFromUrl &&
        !isEditingAssignmentLink
      ) {
        const pad = lead.parsedAssignmentData;
        const prioritySections = [
          {
            key: "req",
            title: "Required technical skills",
            items: pad.skills?.technical_required,
            type: "skills" as const,
          },
          {
            key: "opt",
            title: "Optional technical skills",
            items: pad.skills?.technical_optional,
            type: "skills" as const,
          },
          {
            key: "ksf",
            title: "Key success factors",
            items: pad.keySuccessFactors,
            type: "list" as const,
          },
          {
            key: "mh",
            title: "Must haves",
            items: pad.mustHaveQualifiers,
            type: "list" as const,
          },
        ].filter((s) => s.items && s.items.length > 0).slice(0, 2);

        // Parsed data fully loaded
        return (
          <>
            <LeadRowCardSection title="Role" secondarySection>
              <Input
                leftIcon={faBriefcase}
                label="Role"
                transformValue={trim}
                onChange={(e) => setLocalAssignmentTitle(e.target.value)}
                value={localAssignmentTitle}
                onBlur={() => {
                  if (lead.parsedAssignmentData) {
                    onMutateLead({
                      title: localAssignmentTitle,
                      parsedAssignmentData: {
                        ...lead.parsedAssignmentData,
                        title: localAssignmentTitle,
                      },
                    });
                  }
                }}
              />
            </LeadRowCardSection>
            {lead.parsedAssignmentData.summary && (
              <p className="text-sm text-gray-600 leading-snug">
                {lead.parsedAssignmentData.summary}
              </p>
            )}
            <Button
              variant="secondary"
              className="self-center"
              onClick={() => {
                homeState.assignmentDrawerLeadId = lead.id ?? null;
              }}
            >
              View assignment info
            </Button>
          </>
        );
      }
      // parsedAssignmentData exists but editing or no skills yet
      return (
        <LeadRowCardSection>
          <Input
            disabled={disabled}
            leftIcon={faGlobe}
            label="Link"
            validate={isUrlOrEmpty}
            value={lead.parsedAssignmentData.sourceUrl || ""}
            rightButton={{ label: "Go!", onClick: onRequestAssignmentInfo }}
          />
          <SecondarySectionTitle className="text-sm">OR</SecondarySectionTitle>
          <Input
            disabled={disabled}
            leftIcon={faGlobe}
            label="Paste assignment text"
            validate={isString}
            value={lead.parsedAssignmentData.sourceText || ""}
            rows={5}
            rightButton={{
              label: "Go!",
              onClick: onRequestAssignmentInfoWithInfo,
            }}
          />
          {!isEditingAssignmentLink && (
            <Input
              disabled={disabled}
              leftIcon={faBriefcase}
              label="Role"
              transformValue={trim}
              onChange={(e) => setLocalAssignmentTitle(e.target.value)}
              value={localAssignmentTitle}
              onBlur={() => {
                if (lead.parsedAssignmentData) {
                  onMutateLead({
                    parsedAssignmentData: {
                      ...lead.parsedAssignmentData,
                      title: localAssignmentTitle,
                    },
                  });
                }
              }}
              className="mt-4"
            />
          )}
          <AssignmentHint couldNotLoadFromUrl={couldNotLoadFromUrl} />
        </LeadRowCardSection>
      );
    }

    // Legacy assignmentInfo branch
    if (
      lead?.assignmentInfo &&
      lead.assignmentInfo.skills &&
      lead.assignmentInfo.skills.length > 0 &&
      !couldNotLoadFromUrl &&
      !isEditingAssignmentLink
    ) {
      return (
        <>
          <LeadRowCardSectionWithLink
            linkUrl={lead.assignmentInfo.assignmentUrl}
            onEditClick={onEditAssigmentLinkClick}
          />
          {!isEditingAssignmentLink && (
            <LeadRowCardSection title="Role" secondarySection>
              <Input
                leftIcon={faBriefcase}
                label="Role"
                transformValue={trim}
                onChange={(e) => setLocalAssignmentTitle(e.target.value)}
                value={localAssignmentTitle}
                onBlur={() => {
                  if (
                    !(!lead && localAssignmentTitle === "") &&
                    lead.assignmentInfo
                  ) {
                    onMutateLead({
                      title: localAssignmentTitle,
                      assignmentInfo: {
                        ...lead.assignmentInfo,
                        title: localAssignmentTitle,
                      },
                    });
                  }
                }}
              />
            </LeadRowCardSection>
          )}
          {lead.assignmentInfo.skillTags &&
            lead.assignmentInfo.skillTags.length > 0 && (
              <LeadRowCardSection title="Technical Skills" secondarySection>
                <div className="flex flex-row flex-wrap gap-2 px-4">
                  {lead.assignmentInfo.skillTags.map((skill, i) => (
                    <TooltipProvider key={i} delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-6 h-6 flex items-center justify-center">
                            <SkillsIcon profileSkillTag={skill} text="" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="mb-2">
                          <p>{skill?.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
                {skillMatchData && (
                  <LeadRowCard
                    className="flex flex-1 flex-col bg-red-1 text-sm mt-2"
                    type="dashed"
                  >
                    <p>{`${Math.min(skillMatchData.length, 5)} profiles have matching skills`}</p>
                    <div className="flex flex-1 flex-row gap-3 overflow-y-scroll">
                      {skillMatchData.slice(0, 5).map((m) => (
                        <Image
                          key={m.profileId}
                          src={getImageUrl(getProfilePhotoIds(m.profileId!)!)!}
                          className="rounded-xl w-10 h-10 flex-shrink-0"
                          alt="Profile photo"
                        />
                      ))}
                    </div>
                  </LeadRowCard>
                )}
              </LeadRowCardSection>
            )}
          {lead.assignmentInfo.skills.length > 0 && (
            <LeadRowCardSection title="All skills" secondarySection>
              <List ref={skillsParent}>
                {lead.assignmentInfo.skills
                  .slice(0, expanded ? undefined : MAX_VISIBLE_SKILLS)
                  .map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                {lead.assignmentInfo.skills.length > MAX_VISIBLE_SKILLS && (
                  <Button className="mt-2 w-max" onClick={toggleExpanded}>
                    {expanded ? "show less" : "show more"}
                  </Button>
                )}
              </List>
            </LeadRowCardSection>
          )}
          {lead.assignmentInfo.workplace !== "" && (
            <LeadRowCardSection
              title="Workplace"
              secondarySection
              className="text-sm"
            >
              <List>
                <li>{lead.assignmentInfo.workplace}</li>
                <li>{lead.assignmentInfo.workplaceInfo}</li>
              </List>
            </LeadRowCardSection>
          )}
          {lead.assignmentInfo.sector && (
            <LeadRowCardSection title="Industry" secondarySection>
              <List>
                <li>{lead.assignmentInfo.sector}</li>
              </List>
            </LeadRowCardSection>
          )}
          {lead.assignmentInfo.profile !== "" && (
            <LeadRowCardSection
              title="Profile"
              secondarySection
              className="text-sm"
            >
              <List>
                <li>{lead.assignmentInfo.profile}</li>
              </List>
            </LeadRowCardSection>
          )}
          {lead.assignmentInfo.responsibilities &&
            lead.assignmentInfo.responsibilities.length > 0 && (
              <LeadRowCardSection title="Responsibilities" secondarySection>
                <List>
                  {lead.assignmentInfo.responsibilities.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </List>
              </LeadRowCardSection>
            )}
          {lead.assignmentInfo.location &&
            lead.assignmentInfo.location.length > 0 && (
              <LeadRowCardSection title="Location" secondarySection>
                <List>
                  {lead.assignmentInfo.location.map((l) => (
                    <li key={l}>{l}</li>
                  ))}
                </List>
              </LeadRowCardSection>
            )}
          {lead.assignmentInfo.remote && lead.assignmentInfo.remote !== "" && (
            <LeadRowCardSection title="Remote" secondarySection>
              <List>
                <li>{formatRemote(lead.assignmentInfo.remote)}</li>
              </List>
            </LeadRowCardSection>
          )}
        </>
      );
    }

    // No assignment data yet — show input form
    return (
      <LeadRowCardSection>
        <Input
          disabled={disabled}
          leftIcon={faGlobe}
          label="Link"
          validate={isUrlOrEmpty}
          value={lead?.assignmentInfo?.assignmentUrl}
          rightButton={{ label: "Go!", onClick: onRequestAssignmentInfo }}
        />
        <SecondarySectionTitle className="text-sm">OR</SecondarySectionTitle>
        <Input
          disabled={disabled}
          leftIcon={faGlobe}
          label="Paste assignment text"
          validate={isString}
          value={lead?.assignmentInfo?.assignmentDescription}
          rows={5}
          rightButton={{
            label: "Go!",
            onClick: onRequestAssignmentInfoWithInfo,
          }}
        />
        {!isEditingAssignmentLink && (
          <Input
            disabled={disabled}
            leftIcon={faBriefcase}
            label="Role"
            transformValue={trim}
            onChange={(e) => setLocalAssignmentTitle(e.target.value)}
            value={localAssignmentTitle}
            onBlur={() => {
              if (lead?.assignmentInfo) {
                onMutateLead({
                  assignmentInfo: {
                    ...lead.assignmentInfo,
                    title: localAssignmentTitle,
                  },
                });
              } else if (localAssignmentTitle.trim() !== "") {
                onMutateLead({
                  assignmentInfo: {
                    title: localAssignmentTitle,
                    skills: [],
                    allSkills: [],
                    responsibilities: [],
                    location: [],
                    remote: "",
                    workplace: "",
                    workplaceInfo: "",
                    profile: "",
                    assignmentUrl: "",
                  },
                });
              }
            }}
            className="mt-4"
          />
        )}
        <AssignmentHint couldNotLoadFromUrl={couldNotLoadFromUrl} />
      </LeadRowCardSection>
    );
  })();

  // -------------------------------------------------------------------------
  // Company section content
  // -------------------------------------------------------------------------

  const companyContent =
    lead?.companyUrl && !isEditingWebsiteLink ? (
      <>
        <LeadRowCardSectionWithLink
          linkUrl={lead.companyUrl}
          onEditClick={onEditWebsiteLinkClick}
        />
        <LeadRowCardSection title="Name" secondarySection>
          <Input
            disabled={disabled}
            leftIcon={faGlobe}
            label="Company website link"
            onChange={(e) => setLocalCompanyName(e.target.value)}
            value={localCompanyName}
            onBlur={() => {
              if (!(!lead && localCompanyName === "")) {
                onMutateLead({ companyName: localCompanyName });
              }
            }}
          />
        </LeadRowCardSection>
        <LeadRowCardSection title="Pick image">
          <div className="flex flex-row flex-wrap gap-2">
            {lead.logoSuggestions?.map((logo, i) => (
              <ImageButton
                key={logo}
                active={lead.logoSuggestionOrigin === logo}
                alt={`Logo suggestion ${i}`}
                imageUrl={logo}
                onClick={() => onSelectLogo(logo)}
              />
            ))}
            <TextButton
              active={
                !lead.logoSuggestionOrigin || lead.logoSuggestionOrigin === ""
              }
              onClick={() => onSelectLogo("")}
              text={lead.companyName ? lead.companyName[0] : ""}
            />
          </div>
        </LeadRowCardSection>
        <LeadRowCardSection
          title="Description"
          secondarySection
          className="text-sm"
        >
          {lead.companyDesc}
        </LeadRowCardSection>
      </>
    ) : (
      <>
        <LeadRowCardSection>
          <form onSubmit={handleCompanyUrlSubmit}>
            <Input
              ref={companyUrlInputRef}
              disabled={disabled}
              leftIcon={faGlobe}
              label="Company website link"
              validate={isUrlWithMaxLengthOrEmpty}
              value={lead?.companyUrl}
              rightButton={{
                label: "Go!",
                onClick: () => {
                  if (
                    companyUrlInputRef.current?.value &&
                    companyUrlInputRef.current.value.length < 200
                  ) {
                    onRequestWebsiteInfo(companyUrlInputRef.current.value);
                  }
                },
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onRequestWebsiteInfo(companyUrlInputRef.current?.value || "");
                }
              }}
            />
          </form>
        </LeadRowCardSection>
        {couldNotLoadClientFromUrl && (
          <LeadRowCard className="text-xs bg-red-2 border-red-3" type="dashed">
            <div className="flex flex-1 items-center justify-between gap-3">
              <FontAwesomeIcon
                icon={faFaceFrown}
                className="w-4 h-4 shrink-0"
              />
              <p>
                Something went wrong when fetching company information. Please
                make sure that the url is valid and try again.
              </p>
            </div>
          </LeadRowCard>
        )}
        <LeadRowCard className="text-xs bg-red-1 border-red-2" type="dashed">
          <div className="flex flex-1 items-center gap-3">
            <FontAwesomeIcon
              icon={faMagicWandSparkles}
              className="w-4 h-4 shrink-0"
            />
            <p>Add company domain to fetch info</p>
          </div>
        </LeadRowCard>
      </>
    );

  // -------------------------------------------------------------------------
  // Notes section content
  // -------------------------------------------------------------------------

  const notesContent = (
    <Input
      disabled={disabled}
      leftIcon={faGlobe}
      label="Whatever information you would like..."
      rows={3}
      onChange={(e) => setLocalNotes(e.target.value)}
      value={localNotes}
      onBlur={() => {
        if (!(!lead && localNotes === "")) {
          onMutateLead({ notes: localNotes });
        }
      }}
    />
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const hasAssignmentData =
    (lead?.parsedAssignmentData?.skills &&
      !couldNotLoadFromUrl &&
      !isEditingAssignmentLink) ||
    (lead?.assignmentInfo?.skills &&
      lead.assignmentInfo.skills.length > 0 &&
      !couldNotLoadFromUrl &&
      !isEditingAssignmentLink);

  return (
    <div className="bg-white border border-gray-3 rounded-2xl p-4 flex flex-col gap-3">
      {!hasAssignmentData && (
        <div className="flex flex-row gap-3 items-start">
          <span className="font-bold text-lg">1.</span>
          <div className="flex flex-col">
            <span className="font-bold text-lg">Add assignment</span>
            <span className="text-sm text-gray-6">
              Add assignment information to get match suggestions and optimised
              profile versions.
            </span>
          </div>
        </div>
      )}
      <AccordionSection
        title={hasAssignmentData ? "Assignment" : "Fetch assignment info"}
        defaultOpen
      >
        {assignmentContent}
      </AccordionSection>

      <AccordionSection title="Contact">
        <LeadRowCardSection>
          <Input
            leftIcon={faPerson}
            label="Contact name"
            value={localContactName}
            onChange={(e) => setLocalContactName(e.target.value)}
            onBlur={() => {
              if (!(!lead && localContactName === "")) {
                onMutateLead({ contactPersonName: localContactName });
              }
            }}
            transformValue={trim}
          />
          <Input
            leftIcon={faAt}
            label="E-mail"
            value={lead?.contactPersonEmail}
            onChange={(e) => setLocalContactEmail(e.target.value)}
            onBlur={() => {
              if (!(!lead && localContactEmail === "")) {
                onMutateLead({ contactPersonEmail: localContactEmail });
              }
            }}
            transformValue={trim}
            validate={isEmailOrEmpty}
            type="email"
          />
          <Input
            leftIcon={faPhone}
            label="Phone number"
            value={lead?.contactPersonPhone}
            onChange={(e) => setLocalContactPhone(e.target.value)}
            onBlur={() => {
              if (!(!lead && localContactPhone === "")) {
                onMutateLead({ contactPersonPhone: localContactPhone });
              }
            }}
            transformValue={trim}
            validate={isPhoneNumberOrEmpty}
            type="tel"
          />
        </LeadRowCardSection>
        <LeadRowCardSection title="Notes" secondarySection>
          {notesContent}
        </LeadRowCardSection>
      </AccordionSection>

      <AccordionSection title="Company">{companyContent}</AccordionSection>

      {onArchive && (
        <Button variant="secondary" onClick={onArchive} className="self-center">
          Archive lead
        </Button>
      )}

      {showMatrix && <AccordionSection title="Competence matrix">
        {hasMatrixTemplate ? (
          <div className="flex items-center justify-between px-1">
            <span className="text-sm text-gray-600">File uploaded</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  if (lead?.id) homeState.matrixDrawerLeadId = lead.id;
                }}
                className="text-sm text-red-500 hover:underline"
              >
                View
              </button>
              <button
                type="button"
                onClick={() =>
                  deleteMatrix.mutate(undefined, {
                    onSuccess: () => toast.success("Matrix cleared"),
                    onError: () => toast.error("Failed to clear matrix"),
                  })
                }
                disabled={deleteMatrix.isPending}
                className="text-sm text-gray-400 hover:text-red-500 hover:underline disabled:opacity-50"
              >
                {deleteMatrix.isPending ? "Clearing…" : "Clear"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <input
              ref={matrixFileInputRef}
              type="file"
              accept=".docx"
              className="hidden"
              onChange={handleMatrixUpload}
            />
            <Button
              variant="secondary"
              onClick={() => matrixFileInputRef.current?.click()}
              disabled={uploadMatrix.isPending}
            >
              {uploadMatrix.isPending ? "Uploading..." : "Upload .docx"}
            </Button>
          </>
        )}
      </AccordionSection>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const AssignmentLoadingState = () => (
  <div className="flex flex-col justify-center items-center rounded-2xl relative overflow-hidden min-h-[200px]">
    <style>
      {`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }
      `}
    </style>
    <div
      className="absolute inset-0 rounded-2xl"
      style={{
        background:
          "linear-gradient(90deg, rgba(57,220,234,0.05) 10%, rgba(255,40,166,0.05) 88%)",
        backgroundSize: "200% 100%",
        animation: "gradientShift 3s ease-in-out infinite alternate",
      }}
    />
    <div className="flex flex-row gap-1.5 relative z-10 items-center">
      <img src={MagicIcon} alt="" className="w-6 h-6" />
      <h2 className="font-bold text-gray-6">Preparing assignment</h2>
    </div>
  </div>
);

const AssignmentHint = ({
  couldNotLoadFromUrl,
}: {
  couldNotLoadFromUrl: boolean;
}) =>
  couldNotLoadFromUrl ? (
    <LeadRowCard className="text-xs bg-red-2 border-red-3 mt-3" type="dashed">
      <div className="flex flex-1 items-center justify-between gap-3">
        <FontAwesomeIcon icon={faFaceFrown} className="w-4 h-4 shrink-0" />
        <p>
          Assignment info could not be fetched from the link. Please try to
          paste the assignment description instead.
        </p>
      </div>
    </LeadRowCard>
  ) : null;

const List = forwardRef<HTMLUListElement, PropsWithChildren>(
  ({ children }, ref) => (
    <ul ref={ref} className="list-disc list-inside text-sm px-4">
      {children}
    </ul>
  ),
);
List.displayName = "List";

AssignmentAccordionPanel.displayName = "AssignmentAccordionPanel";
